import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, Phone, Loader2, Send, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (n) => `₹${Number(n).toFixed(2)}`;

export function ServicesPanel({ d, reload }) {
  const active = d.subscription?.status === "active";
  const gst = d.gst_percent ?? 18;
  const qPrice = d.per_question_price ?? 29;
  const qGst = +(qPrice * gst / 100).toFixed(2);
  const qTotal = +(qPrice + qGst).toFixed(2);
  const cBase = d.consultation_fee ?? 999;
  const cGst = +(cBase * gst / 100).toFixed(2);
  const cTotal = +(cBase + cGst).toFixed(2);

  const [q, setQ] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const bp = d.birth_profile;
  const bpText = bp ? [bp.date_of_birth, bp.time_of_birth, [bp.birth_city, bp.birth_state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") : "";
  const [form, setForm] = useState({ name: bp?.full_name || d.user?.name || "", phone: d.user?.phone || "", topic: "", birth_details: bpText, preferred_contact: "Call" });
  const [cBusy, setCBusy] = useState(false);
  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pending = (d.questions || []).some((x) => x.status !== "answered");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [pending, reload]);

  useEffect(() => {
    if (window.location.hash === "#consult") setTimeout(() => document.getElementById("consult")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  }, []);

  const askQuestion = async () => {
    if (!q.trim()) { toast.error("Please enter your question"); return; }
    setQBusy(true);
    try {
      const { data: order } = await api.post("/questions/create-order");
      await new Promise((r) => setTimeout(r, 800)); // mock payment
      await api.post("/questions/submit", { order_id: order.order_id, question_text: q.trim(), razorpay_payment_id: `pay_mock_${Date.now()}` });
      toast.success("Question submitted & paid ✨");
      setQ("");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setQBusy(false); }
  };

  const book = async () => {
    if (!form.name || !form.phone || !form.topic) { toast.error("Please fill name, phone and topic"); return; }
    setCBusy(true);
    try {
      const { data: order } = await api.post("/consultations/create-order");
      await new Promise((r) => setTimeout(r, 900)); // mock payment
      await api.post("/consultations/book", { order_id: order.order_id, ...form, razorpay_payment_id: `pay_mock_${Date.now()}` });
      toast.success("Consultation booked 🔮");
      setForm({ ...form, topic: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCBusy(false); }
  };

  const inp = "w-full rounded-xl bg-[#0B1024] border border-amber-500/20 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400/60 mt-1.5";

  return (
    <div className="mt-10 grid gap-6 lg:grid-cols-2">
      {/* ASK A QUESTION */}
      <div className="mc-card p-6" data-testid="ask-question-panel">
        <div className="flex items-center gap-2 text-amber-300 mb-1"><MessageCircleQuestion size={18} /><span className="text-xs uppercase tracking-[0.15em]">Ask a Question</span></div>
        <p className="text-sm text-slate-400 mb-3">Get a personalized answer based on your computed Vedic birth chart — usually within a minute.</p>
        {active && !d.birth_profile_complete ? (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 px-4 py-3" data-testid="question-birth-required">
            <p className="text-sm text-amber-100">Add your birth details first so your answer is based on your own chart.</p>
            <a href="/onboarding" className="btn-gold rounded-full inline-block px-5 py-2 text-sm mt-3" data-testid="question-add-birth">Add Birth Details</a>
          </div>
        ) : active ? (
          <>
            <div className="rounded-lg bg-[#0B1024]/70 border border-amber-500/10 px-3 py-2 text-[11px] text-slate-400" data-testid="question-profile-used">
              Answer will use your profile: <span className="text-amber-200">{bp.full_name}</span> · {bpText}{bp.sun_sign ? ` · ${bp.sun_sign}` : ""}
            </div>
            <textarea data-testid="question-input" rows={3} value={q} onChange={(e) => setQ(e.target.value)} className={inp} placeholder="Type your astrology question…" />
            <div className="mt-3 rounded-xl bg-amber-500/5 border border-amber-500/15 px-4 py-3 text-xs text-slate-300 space-y-1">
              <div className="flex justify-between"><span>Question charge</span><span>{money(qPrice)}</span></div>
              <div className="flex justify-between"><span>GST ({gst}%)</span><span>{money(qGst)}</span></div>
              <div className="flex justify-between text-amber-100 font-semibold border-t border-amber-500/15 pt-1 mt-1"><span>Total payable</span><span data-testid="question-total">{money(qTotal)}</span></div>
            </div>
            <button data-testid="ask-question-pay" onClick={askQuestion} disabled={qBusy} className="btn-gold rounded-full w-full mt-3 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {qBusy ? <><Loader2 size={15} className="animate-spin" /> Processing…</> : <><Send size={15} /> Pay {money(qTotal)} & Submit</>}
            </button>
          </>
        ) : (
          <p className="text-sm text-slate-400">An active subscription is required to ask questions. Your per-question rate depends on your plan.</p>
        )}

        {(d.questions || []).length > 0 && (
          <div className="mt-5 space-y-3" data-testid="question-history">
            <p className="text-[11px] uppercase tracking-wider text-amber-400/90">Your Questions</p>
            {d.questions.map((item) => (
              <div key={item.id} className="rounded-xl border border-amber-500/12 bg-[#0B1024]/60 px-4 py-3" data-testid={`question-item-${item.id}`}>
                <p className="text-sm text-slate-200">{item.question_text}</p>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                  <span>{money(item.amount)}</span>
                  <span data-testid={`question-status-${item.id}`} className={`flex items-center gap-1 ${item.status === "answered" ? "text-emerald-300" : "text-amber-300"}`}>
                    {item.status === "answered" ? "Answered" : <><Loader2 size={11} className="animate-spin" /> Reading your chart…</>}
                  </span>
                  {item.chart_based && <span className="text-slate-400">· Based on your birth chart</span>}
                </div>
                {item.answer_text && (
                  <div className="mt-2 rounded-lg bg-amber-500/5 border border-amber-500/15 px-3 py-2 text-sm text-slate-300">
                    <span className="text-[10px] uppercase tracking-wider text-amber-400/90 block mb-0.5">Your answer</span>
                    <p className="whitespace-pre-line" data-testid={`question-answer-${item.id}`}>{item.answer_text}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CONSULT AN ASTROLOGER */}
      <div id="consult" className="mc-card p-6 scroll-mt-24" data-testid="consult-panel">
        <div className="flex items-center gap-2 text-amber-300 mb-1"><Phone size={18} /><span className="text-xs uppercase tracking-[0.15em]">Consult an Astrologer</span></div>
        <p className="text-sm text-slate-400 mb-1 flex items-center gap-1.5"><Clock size={13} /> An astrologer will get back to you within 24 hours of your confirmed booking.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <input data-testid="consult-name" value={form.name} onChange={setF("name")} className={inp} placeholder="Your name" />
          <input data-testid="consult-phone" value={form.phone} onChange={setF("phone")} className={inp} placeholder="Phone number" />
        </div>
        <input data-testid="consult-topic" value={form.topic} onChange={setF("topic")} className={inp} placeholder="Consultation topic" />
        <input data-testid="consult-birth" value={form.birth_details} onChange={setF("birth_details")} className={inp} placeholder="Birth details (optional)" />
        <select data-testid="consult-contact" value={form.preferred_contact} onChange={setF("preferred_contact")} className={inp}>
          <option>Call</option><option>WhatsApp</option><option>Video Call</option>
        </select>
        <div className="mt-3 rounded-xl bg-amber-500/5 border border-amber-500/15 px-4 py-3 text-xs text-slate-300 space-y-1">
          <div className="flex justify-between"><span>Consultation fee</span><span>{money(cBase)}</span></div>
          <div className="flex justify-between"><span>GST ({gst}%)</span><span>{money(cGst)}</span></div>
          <div className="flex justify-between text-amber-100 font-semibold border-t border-amber-500/15 pt-1 mt-1"><span>Total payable</span><span data-testid="consult-total">{money(cTotal)}</span></div>
        </div>
        <button data-testid="consult-pay" onClick={book} disabled={cBusy} className="btn-gold rounded-full w-full mt-3 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {cBusy ? <><Loader2 size={15} className="animate-spin" /> Processing…</> : <><Phone size={15} /> Pay {money(cTotal)} & Book</>}
        </button>

        {(d.consultations || []).length > 0 && (
          <div className="mt-5 space-y-2" data-testid="consult-history">
            <p className="text-[11px] uppercase tracking-wider text-amber-400/90">Your Bookings</p>
            {d.consultations.map((c) => (
              <div key={c.id} className="rounded-xl border border-amber-500/12 bg-[#0B1024]/60 px-4 py-3 flex items-center justify-between" data-testid={`consult-item-${c.id}`}>
                <div>
                  <p className="text-sm text-slate-200">{c.topic}</p>
                  <p className="text-[11px] text-slate-500">{money(c.amount)} · {new Date(c.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                <span className="flex items-center gap-1 text-[11px] text-amber-200 capitalize"><CheckCircle2 size={13} /> {c.status.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
